import { Link, useHistory, useParams } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import * as Meter from '@givebutter/primitives/meter';
import { faCircleCheck, faTicket } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchSeatingGroup } from 'api/events';
import * as Drawer from 'components/drawer';
import * as DropdownMenu from 'components/dropdown-menu';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { eventQueryKeys } from 'queries/event-query-keys';
import { twMerge } from 'tailwind-merge';
import { Ticket } from 'types/domain';

const ViewSeatingGroupDrawer = () => {
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const { data: event } = useCurrentCampaignEvent();
    const history = useHistory();

    const { seatingGroupId } = useParams<{ seatingGroupId: string }>();

    const seatingGroupIdInt = parseInt(seatingGroupId);

    const { data: seatingGroup } = useSuspenseQuery({
        queryKey: eventQueryKeys.seatingGroup(event.id, seatingGroupIdInt),
        queryFn: () => fetchSeatingGroup(event.id, seatingGroupIdInt),
    });

    const capacity = parseInt(seatingGroup.capacity) || 0;
    const checkedInCount = seatingGroup.tickets?.filter(filterTicketsOnlyCheckedIn).length || 0;

    const filledPercentage =
        capacity > 0 ? Math.min(Math.round((checkedInCount / capacity) * 100), 100) : 0;

    return (
        <>
            <Drawer.Header>
                <Drawer.Title>
                    {seatingGroup.name ?? `Seating Group ${seatingGroup.number}`}
                </Drawer.Title>
                <Drawer.Close />
            </Drawer.Header>
            <Drawer.Body className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                        <div>
                            <FontAwesomeIcon icon={faCircleCheck} className={'text-green-500'} />
                        </div>
                        <p className="text-lg font-medium">
                            <span>{checkedInCount}</span>
                            <span>/</span>
                            <span>{seatingGroup.capacity}</span>
                            <span> Attendees checked-in</span>
                        </p>
                    </div>
                    <div>
                        <Meter.Root
                            type="line"
                            value={filledPercentage}
                            aria-label="Checked-in Percentage"
                            size="md"
                            variant="info"
                            className="w-full flex-shrink-0"
                        >
                            <Meter.LineTrack className="bg-gray-200">
                                <Meter.LineIndicator />
                            </Meter.LineTrack>
                        </Meter.Root>
                    </div>
                </div>
                <div className={'flex flex-col justify-between gap-4'}>
                    {seatingGroup.tickets?.map((ticket: Omit<Ticket, 'seating_group'>) => {
                        const ticketUrl = `/accounts/${accountId}/campaigns/${campaign.id}/event/table-seating/${seatingGroup.id}/ticket/${ticket.id}`;
                        return (
                            <div key={ticket.id} className="flex items-center gap-2">
                                <Link to={ticketUrl} className="flex grow items-center gap-2">
                                    <p className="text-md w-4 text-gray-500">
                                        {ticket.seat_number}
                                    </p>
                                    <Avatar.Root
                                        size="md"
                                        className={twMerge(
                                            'border-2 bg-gray-100',
                                            ticket.checked_in_at
                                                ? 'border-green-500'
                                                : 'border-gray-300',
                                        )}
                                    >
                                        <Avatar.Fallback
                                            aria-label={`${ticket.first_name} ${ticket.last_name}`}
                                        >
                                            <span className="font-medium tracking-tighter">
                                                {ticket.first_name?.[0]} {ticket.last_name?.[0]}
                                            </span>
                                        </Avatar.Fallback>
                                    </Avatar.Root>
                                    <div className="flex grow flex-col">
                                        <p className="text-sm font-bold">{ticket.name}</p>
                                        <p className="text-xs text-gray-500">{ticket.type}</p>
                                    </div>
                                </Link>
                                <div className="flex w-2 items-center justify-end">
                                    <DropdownMenu.Root>
                                        <DropdownMenu.EllipsisTrigger className="px-2 py-1" />
                                        <DropdownMenu.Content align={'end'}>
                                            <DropdownMenu.Item
                                                onClick={e => {
                                                    e.stopPropagation();

                                                    history.push(ticketUrl);
                                                }}
                                            >
                                                <div className={'flex items-center gap-x-1.5'}>
                                                    <FontAwesomeIcon
                                                        className={'flex size-4'}
                                                        icon={faTicket}
                                                    />
                                                    <p>View Ticket</p>
                                                </div>
                                            </DropdownMenu.Item>
                                        </DropdownMenu.Content>
                                    </DropdownMenu.Root>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Drawer.Body>
            <Drawer.Footer>
                <div className={'flex w-full flex-wrap-reverse items-center justify-between gap-2'}>
                    <Drawer.Cancel>Close</Drawer.Cancel>
                </div>
            </Drawer.Footer>
        </>
    );
};

const filterTicketsOnlyCheckedIn = (ticket: Omit<Ticket, 'seating_group'>) =>
    ticket.checked_in_at !== null;

export { ViewSeatingGroupDrawer };
